import { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';

import * as S from './AgentDropdown.styled';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/dropdown-arrow.svg';
import type { Agent } from 'types';

interface AgentDropdownProps {
    agents: Agent[];
    defaultAgent?: number;
    placeholder?: string;
    onChange: (agentId: number) => Promise<boolean> | void;
}

const AgentDropdown = ({
    agents = [],
    defaultAgent,
    placeholder = '--',
    onChange,
}: AgentDropdownProps) => {
    const [selected, setSelected] = useState<Agent | null>(() => {
        return agents.find((a) => a.id === defaultAgent) || null;
    });
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedButton = buttonRef.current?.contains(target);
            const clickedDropdown = dropdownRef.current?.contains(target);
            if (!clickedButton && !clickedDropdown) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (open) {
            setSearch('');
            const id = requestAnimationFrame(() => searchInputRef.current?.focus());
            return () => cancelAnimationFrame(id);
        }
    }, [open]);

    const filteredAgents = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return agents;
        return agents.filter((agent) =>
            `${agent.firstname ?? ''} ${agent.lastname ?? ''}`.toLowerCase().includes(term)
        );
    }, [agents, search]);

    const handleToggleDropdown = () => {
        if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
            setOpen(true);
        } else {
            setOpen(false);
        }
    };

    const dropdown =
        open && position
            ? ReactDOM.createPortal(
                  <S.PortalDropdown
                      ref={dropdownRef}
                      style={{ top: `${position.top}px`, left: `${position.left}px` }}
                      onMouseDown={(e) => e.stopPropagation()}
                  >
                      <S.SearchInput
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search agent..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                      />
                      <S.DropdownList>
                          {filteredAgents.length === 0 ? (
                              <S.EmptyState>No agents found</S.EmptyState>
                          ) : (
                              filteredAgents.map((agent) => (
                                  <S.DropdownItem
                                      key={agent.id}
                                      onClick={async () => {
                                          const previous = selected;
                                          setSelected(agent);
                                          setOpen(false);
                                          const result = onChange(agent.id);
                                          if (result instanceof Promise) {
                                              const success = await result;
                                              if (!success) {
                                                  setSelected(previous);
                                              }
                                          }
                                      }}
                                  >
                                      <span>
                                          {agent.firstname} {agent.lastname}
                                      </span>
                                  </S.DropdownItem>
                              ))
                          )}
                      </S.DropdownList>
                  </S.PortalDropdown>,
                  document.body
              )
            : null;

    return (
        <S.Container>
            <S.Button ref={buttonRef} onClick={handleToggleDropdown} style={{}}>
                <S.Text data={selected}>
                    {selected ? `${selected.firstname} ${selected.lastname}` : placeholder}
                </S.Text>
                <ArrowDownIcon
                    fill={selected ? '#282828' : '#6b7280'}
                    style={{
                        width: '12px',
                        height: '12px',
                        flexShrink: 0,
                        transform: open ? 'rotate(180deg)' : 'rotate(0)',
                        transition: '0.2s ease',
                    }}
                />
            </S.Button>
            {dropdown}
        </S.Container>
    );
};

export default AgentDropdown;
